import React from 'react'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  expediente: ExpedienteDespacho
  mostrarSelloNaviera?: boolean
}

export const HojaBillOfLading: React.FC<Props> = ({ expediente, mostrarSelloNaviera = true }) => {
  const lineaNaviera = expediente.contenedor.lineaNaviera || 'MEDITERRANEAN SHIPPING COMPANY'
  const blNumero = expediente.documentos.bl.numero || `BL-MSC-${expediente.folio.slice(-5)}`
  const booking = `BKG-ZLO-${expediente.folio.slice(-6)}`

  return (
    <div className="relative w-full bg-[#fcfbf7] text-slate-900 shadow-2xl rounded-sm p-6 sm:p-10 font-sans border border-slate-300 print:border-none print:shadow-none print:p-4 text-[10px] sm:text-[11px] leading-tight select-none">
      {/* Marca de Agua Marítima */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] overflow-hidden">
        <div className="text-[100px] font-black uppercase text-blue-900 -rotate-30 select-none tracking-widest whitespace-nowrap">
          ORIGINAL BILL OF LADING
        </div>
      </div>

      {/* Encabezado Naviero */}
      <div className="border-b-2 border-slate-900 pb-3 mb-2 flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded bg-blue-900 text-white font-black text-base flex items-center justify-center font-serif">
              M
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black tracking-tight text-blue-950 uppercase font-serif">
                {lineaNaviera} S.A.
              </h2>
              <p className="text-[8px] tracking-widest font-mono text-slate-500 uppercase">
                GENEVA, SWITZERLAND · GLOBAL OCEAN TRANSPORT SERVICES
              </p>
            </div>
          </div>
          <p className="text-[9px] font-black uppercase text-slate-800 tracking-wider">
            NEGOTIABLE MULTIMODAL TRANSPORT BILL OF LADING
          </p>
        </div>

        <div className="text-right">
          <span className="font-mono text-xs font-black text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded">
            ORIGINAL / COPY 1 OF 3
          </span>
          <p className="font-mono text-xs sm:text-sm font-black text-slate-950 mt-1 tracking-wider">
            B/L NO: <span className="text-blue-950">{blNumero}</span>
          </p>
          <p className="font-mono text-[9px] text-slate-500">BOOKING REF: {booking}</p>
        </div>
      </div>

      {/* Cuadrante 1: Shipper, Consignee, Notify */}
      <div className="border-2 border-slate-900 grid grid-cols-12 divide-x-2 divide-slate-900 mb-2">
        {/* Columna Izquierda: Actores */}
        <div className="col-span-7 divide-y-2 divide-slate-900">
          {/* Shipper */}
          <div className="p-2 min-h-[64px]">
            <span className="block text-[8px] font-bold uppercase text-slate-500">
              1. SHIPPER / EXPORTER (COMPLETE NAME & ADDRESS)
            </span>
            <p className="font-bold text-xs text-slate-950 uppercase">{expediente.actores.emisor.nombre}</p>
            <p className="text-[9px] text-slate-600 font-mono">
              TAX ID / RFC: {expediente.actores.emisor.rfc} · {expediente.actores.emisor.pais}
            </p>
            <p className="text-[8.5px] text-slate-500 truncate">
              PARQUE INDUSTRIAL LOGÍSTICO MANZANILLO, COLIMA, MÉXICO. CP 28219.
            </p>
          </div>

          {/* Consignee */}
          <div className="p-2 min-h-[64px] bg-slate-50/50">
            <span className="block text-[8px] font-bold uppercase text-slate-500">
              2. CONSIGNEE (OR ORDER OF)
            </span>
            <p className="font-bold text-xs text-slate-950 uppercase">{expediente.actores.receptor.nombre}</p>
            <p className="text-[9px] text-slate-700 font-mono">
              VAT / TAX ID: <strong className="text-slate-950">{expediente.actores.receptor.rfc}</strong>
            </p>
            <p className="text-[8.5px] text-slate-500">{expediente.actores.receptor.domicilio}</p>
          </div>

          {/* Notify Party */}
          <div className="p-2 min-h-[48px]">
            <span className="block text-[8px] font-bold uppercase text-slate-500">
              3. NOTIFY PARTY (CARRIER NOT RESPONSIBLE FOR FAILURE TO NOTIFY)
            </span>
            <p className="font-bold text-[10px] text-slate-800 uppercase">
              {expediente.actores.receptor.nombre} (ATTN: IMPORT DEPT.)
            </p>
            <p className="text-[8px] text-slate-500">SAME AS CONSIGNEE · AGENT: {expediente.actores.agenteAduanal.nombre}</p>
          </div>
        </div>

        {/* Columna Derecha: Rutas, Buque y Puertos */}
        <div className="col-span-5 divide-y divide-slate-300 font-mono text-[9px]">
          <div className="p-2">
            <span className="block text-[7.5px] font-sans font-bold uppercase text-slate-500">
              PRE-CARRIAGE BY (INLAND ROUTE)
            </span>
            <p className="font-bold text-slate-900">{expediente.actores.transportista.razonSocial}</p>
          </div>

          <div className="p-2">
            <span className="block text-[7.5px] font-sans font-bold uppercase text-slate-500">
              OCEAN VESSEL & VOYAGE NO.
            </span>
            <p className="font-bold text-xs text-blue-900 uppercase">
              {expediente.buque.nombre} / V.{expediente.buque.viaje}
            </p>
          </div>

          <div className="p-2">
            <span className="block text-[7.5px] font-sans font-bold uppercase text-slate-500">
              PORT OF LOADING (POL)
            </span>
            <p className="font-bold text-slate-900 uppercase">
              MANZANILLO, COLIMA, MÉXICO (MXZLO) · {expediente.buque.terminal}
            </p>
          </div>

          <div className="p-2 bg-slate-50/50">
            <span className="block text-[7.5px] font-sans font-bold uppercase text-slate-500">
              PORT OF DISCHARGE (POD) & PLACE OF DELIVERY
            </span>
            <p className="font-bold text-slate-950 uppercase">{expediente.buque.puertoDestino}</p>
          </div>
        </div>
      </div>

      {/* Tabla Central de Carga: Contenedor, Bultos, Descripción y Peso */}
      <div className="border-2 border-slate-900 mb-2">
        <div className="bg-slate-100 border-b border-slate-900 grid grid-cols-12 divide-x divide-slate-400 font-bold text-[8px] text-slate-700 uppercase p-1">
          <div className="col-span-3">CONTAINER & SEAL NOS.</div>
          <div className="col-span-2 text-center">NO. OF PACKAGES</div>
          <div className="col-span-4">DESCRIPTION OF GOODS & REMARKS</div>
          <div className="col-span-2 text-right">GROSS WEIGHT</div>
          <div className="col-span-1 text-right">MEAS.</div>
        </div>

        <div className="grid grid-cols-12 divide-x divide-slate-300 font-mono text-[9px] p-2 min-h-[140px] bg-white">
          {/* Contenedor y Sellos */}
          <div className="col-span-3 pr-2 space-y-1">
            <p className="font-black text-xs text-blue-950 tracking-wider">
              {expediente.contenedor.numero}
            </p>
            <p className="text-[8px] text-slate-600 font-bold">TYPE: {expediente.contenedor.tipo}</p>
            <div className="p-1 rounded bg-slate-100 border border-slate-200">
              <span className="block text-[7px] font-sans font-bold uppercase text-slate-500">CUSTOMS & CARRIER SEALS:</span>
              <p className="text-[8px] font-bold text-slate-900">{expediente.contenedor.sellos}</p>
            </div>
            <p className="text-[7.5px] text-slate-500">TARA: {expediente.contenedor.taraKg.toLocaleString()} KGS</p>
          </div>

          {/* Número de Bultos */}
          <div className="col-span-2 px-2 text-center font-bold">
            <p className="text-xs text-slate-900">{expediente.documentos.packingList.bultos.toLocaleString()}</p>
            <p className="text-[8px] text-slate-500 uppercase font-sans">PACKAGES / CARTONS ON 20 PALLETS</p>
          </div>

          {/* Descripción de Mercancía */}
          <div className="col-span-4 px-2 space-y-1">
            <p className="font-bold text-xs text-slate-950 uppercase">{expediente.titulo}</p>
            <p className="text-[8px] text-slate-600 uppercase">
              CLEAN ON BOARD · SHIPPED UNDER SHIPPER'S LOAD, STOW AND COUNT (SLAC).
            </p>
            <p className="text-[8px] text-slate-600">
              INCOTERM: <strong>{expediente.documentos.factura.incoterm}</strong>
            </p>
            {expediente.contenedor.tipo === '40RF' && (
              <p className="text-[7.5px] text-blue-800 font-bold bg-blue-50 p-1 rounded border border-blue-200">
                TEMPERATURE SETPOINT: +5.5°C · VENTILATION 15 CBM/H · CONTINUOUS GENSET OPERATION.
              </p>
            )}
          </div>

          {/* Peso Bruto */}
          <div className="col-span-2 px-2 text-right font-mono font-bold">
            <p className="text-xs text-slate-950">
              {expediente.contenedor.pesoBrutoKg.toLocaleString()}.00
            </p>
            <p className="text-[8px] text-slate-500 font-sans">KILOGRAMS</p>
            <p className="text-[7.5px] text-slate-400 mt-2">
              NET: {expediente.documentos.packingList.pesoNetoKg.toLocaleString()} KG
            </p>
          </div>

          {/* Medida CBM */}
          <div className="col-span-1 pl-1 text-right font-mono font-bold">
            <p className="text-xs text-slate-950">68.4</p>
            <p className="text-[7.5px] text-slate-500 font-sans">CBM</p>
          </div>
        </div>
      </div>

      {/* Términos de Flete y Cláusulas */}
      <div className="border border-slate-900 grid grid-cols-12 divide-x divide-slate-300 font-mono text-[8px] mb-2 bg-slate-50">
        <div className="col-span-3 p-1.5">
          <span className="font-sans font-bold uppercase text-[7px] text-slate-500 block">FREIGHT & CHARGES:</span>
          <span className="font-bold text-xs text-emerald-800">FREIGHT PREPAID</span>
        </div>
        <div className="col-span-3 p-1.5">
          <span className="font-sans font-bold uppercase text-[7px] text-slate-500 block">REVENUE TONS:</span>
          <span className="font-bold text-slate-800">24.85 MT</span>
        </div>
        <div className="col-span-3 p-1.5">
          <span className="font-sans font-bold uppercase text-[7px] text-slate-500 block">NO. OF ORIGINAL BS/L:</span>
          <span className="font-bold text-slate-800">THREE (3)</span>
        </div>
        <div className="col-span-3 p-1.5">
          <span className="font-sans font-bold uppercase text-[7px] text-slate-500 block">PLACE & DATE OF ISSUE:</span>
          <span className="font-bold text-slate-800">MANZANILLO, 04/OCT/2026</span>
        </div>
      </div>

      {/* Firmas y Cláusulas Finales */}
      <div className="border-2 border-slate-900 p-2 font-mono text-[7px] relative bg-white">
        <div className="grid grid-cols-12 gap-3 items-center">
          <div className="col-span-8 space-y-1 text-slate-500 font-sans leading-tight">
            <p>
              RECEIVED by the Carrier the Goods as specified above in apparent good order and condition unless otherwise stated, to be transported subject to all the terms and conditions contained in this Bill of Lading and Carrier's applicable tariff. IN WITNESS WHEREOF the number of original Bills of Lading stated above have been signed, one of which being accomplished, the others shall be void.
            </p>
            <p className="font-mono text-[6.5px] text-slate-400">
              SOLAS CHAPTER VI REG. 2 COMPLIANT · VERIFIED GROSS MASS (VGM) RECEIVED & VALIDATED.
            </p>
          </div>

          <div className="col-span-4 border-l border-slate-300 pl-3 text-center">
            <div className="h-10 flex items-center justify-center">
              <span className="font-serif italic text-lg text-blue-900 tracking-tight select-none">
                Capt. Hans J. Weber
              </span>
            </div>
            <div className="border-t border-slate-800 pt-0.5">
              <span className="font-sans font-bold text-[7.5px] uppercase text-slate-800 block">
                SIGNED AS AGENT FOR THE CARRIER
              </span>
              <span className="text-[6.5px] text-slate-500 font-mono">
                {lineaNaviera} AGENCY MEXICO S.A. DE C.V.
              </span>
            </div>
          </div>
        </div>

        {/* Sello de Goma Naviero Azul Redondo */}
        {mostrarSelloNaviera && (
          <div className="absolute right-32 top-[-28px] pointer-events-none transform rotate-12">
            <div className="h-24 w-24 rounded-full border-2 border-dashed border-blue-800 bg-blue-50/70 backdrop-blur-sm p-1 flex flex-col items-center justify-center text-center text-blue-900 shadow-md">
              <span className="text-[6px] font-black uppercase tracking-wider">{lineaNaviera}</span>
              <span className="text-[8px] font-black uppercase text-blue-950">OCEAN CARRIER</span>
              <span className="text-[6px] font-mono font-bold">MANZANILLO PORT</span>
              <span className="text-[5.5px] text-blue-700">04 OCT 2026 · SHIPPED</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
